import React from 'react';
import "./chat-widget-styles.css";
import { AuthProvider } from '@/context/auth';
import ReactDOM from 'react-dom/client';
import './utils/axios';
import { ErrorBoundary } from "@sentry/react";
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/config/query-client';
import { soundNotificationService } from '@/services/SoundNotificationService';
import GuestChatBox from '@/components/Chat/GuestChatBox';
import '@/utils/chat-sentry';

interface ChatWidgetOptions {
    autoOpen?: boolean;
    position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
    theme?: string;
    assetsPath?: string; // Custom path for assets (sounds, etc.)
    [key: string]: any;
}

// Get the base path for assets from the script location
function getAssetsBasePath(customPath?: string): string {
    if (customPath) {
        return customPath;
    }
    
    // Try to detect from current script location
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i--) {
        const src = scripts[i].src;
        if (src && src.includes('chat-widget')) {
            // Extract directory from script URL
            const url = new URL(src);
            const pathParts = url.pathname.split('/');
            pathParts.pop(); // Remove filename
            return url.origin + pathParts.join('/') + '/';
        }
    }
    
    // Default to root for development
    return '/';
}

class ChatWidget {
    private static instance: ReactDOM.Root | null = null;
    private static cssLoaded: boolean = false;

    private static loadCSS(assetsPath: string): void {
        if (ChatWidget.cssLoaded) {
            console.log('CSS already loaded');
            return;
        }

        const cssPath = `${assetsPath}assets/style.css`;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssPath;
        link.onload = () => {
            console.log('Chat widget CSS loaded successfully');
            ChatWidget.cssLoaded = true;
        };
        link.onerror = () => {
            console.error('Failed to load chat widget CSS from:', cssPath);
        };
        document.head.appendChild(link);
        console.log('Loading CSS from:', cssPath);
    }

    static init(options: ChatWidgetOptions = {}) {
        try {
            // Prevent duplicate initialization
            if (document.getElementById('chatbot-root')) {
                console.warn('ChatWidget is already initialized');
                return;
            }
            
            console.log('Initializing ChatWidget with options:', options);
            
            // Set up assets path for sounds and other resources
            const assetsPath = getAssetsBasePath(options.assetsPath);
            console.log('Assets path:', assetsPath);
            
            const element = document.querySelector('script[src="https://cac-central.cac.edu.au/widgets/*"]')
            if(element) {
                console.log('Chat widget script found');
            }
            // Load CSS
            ChatWidget.loadCSS(assetsPath);
            
            soundNotificationService.setBasePath(assetsPath);
            
            const container = document.createElement('div');
            container.id = 'chatbot-root';
            container.style.position = 'fixed';
            container.style.zIndex = '9999';
            
            // Set position based on options
            const position = options.position || 'bottom-right';
            switch(position) {
                case 'bottom-right':
                    container.style.bottom = '0';
                    container.style.right = '0';
                    break;
                case 'bottom-left':
                    container.style.bottom = '0';
                    container.style.left = '0';
                    break;
                case 'top-right':
                    container.style.top = '0';
                    container.style.right = '0';
                    break;
                case 'top-left':
                    container.style.top = '0';
                    container.style.left = '0';
                    break;
            }
            
            document.body.appendChild(container);
            console.log('Chat widget container appended to body');

            const root = ReactDOM.createRoot(container);
            ChatWidget.instance = root;
            
            root.render(
                <React.StrictMode>
                    <QueryClientProvider client={queryClient} >
                        <AuthProvider>
                            <ErrorBoundary 
                                fallback={() => (
                                    <div style={{ padding: '20px', color: 'red', background: 'white', border: '1px solid red' }}>
                                        <h3>Chat Widget Error</h3>
                                        {/* <p>{error?.message || 'An error occurred'}</p> */}
                                    </div>
                                )}
                                onError={(error, errorInfo) => {
                                    console.error('Chat widget error:', error, errorInfo);
                                }}
                            >
                                <GuestChatBox />
                            </ErrorBoundary>
                        </AuthProvider>
                    </QueryClientProvider>
                </React.StrictMode>
            );
            
            console.log('Chat widget initialized successfully');
        } catch (error) {
            console.error('Failed to initialize chat widget:', error);
        }
    }

    static destroy() {
        const container = document.getElementById('chatbot-root');
        if (container) {
            if (ChatWidget.instance) {
                ChatWidget.instance.unmount();
                ChatWidget.instance = null;
            }
            container.remove();
        }
    }
}

// Auto-initialize if data-auto-init attribute is present or autoInit is set
const initializeWidget = () => {
    // Check if auto-init is disabled
    const scripts = document.getElementsByTagName('script');
    let autoInit = true;
    
    for (let i = 0; i < scripts.length; i++) {
        const src = scripts[i].src;
        if (src && src.includes('chat-widget')) {
            const dataAutoInit = scripts[i].getAttribute('data-auto-init');
            if (dataAutoInit === 'false') {
                autoInit = false;
                break;
            }
        }
    }
    
    if (autoInit) {
        console.log('Auto-initializing chat widget');
        ChatWidget.init({});
    } else {
        console.log('Auto-init disabled. Call ChatWidget.init() manually.');
    }
};

// Wait for DOM to be ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWidget);
} else {
    // DOM is already ready
    initializeWidget();
}

// Expose to window for manual initialization
declare global {
    interface Window {
        ChatWidget: typeof ChatWidget;
    }
}

window.ChatWidget = ChatWidget;

export default ChatWidget;
